import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { GoogleDriveFile, VideoCompilerCompilation } from '@/types/resources';
import { api } from '@/utils/api';
import {
  VideoCompilerCompilationStatus,
  VideoCompilerCompilationType,
} from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

import Details from './components/Details';
import RecipeCard from './components/RecipeCard';
import SearchCard from './components/SearchCard';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 32px;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const StyledPageHeading = styled(PageHeading)`
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  span {
    text-transform: capitalize;
  }
`;

const HeaderButton = styled(Button)`
  background: transparent;
`;

const SearchIcon = styled.svg`
  position: absolute;
  left: 16px;
  top: 11px;
  color: ${({ theme }) => theme.colors.gray};
`;

const SearchInput = styled(Text)`
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  height: 40px;
  padding: 0 40px;
  align-items: center;
  display: flex;
  outline: none;
  transition: 0.2s ease-out border-color;
  &::placeholder {
    color: ${({ theme }) => theme.colors.gray};
  }
  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

const TopBar = styled.div`
  display: flex;
  gap: 12px;
  margin: 0 0 12px;
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
`;

const Grid = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  padding-bottom: 16px;
  overflow: hidden;
`;

const LeftCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RightCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
  overflow: hidden;
`;

const availableTypes = ['test', 'final'];
const availableCompilationTypes = ['version', 'compilation'];

export type Part = {
  partId: string;
} & (
  | {
      type: 'tip' | 'version';
      source: 'drive';
      fileData: GoogleDriveFile;
    }
  | {
      type: 'version' | 'compilation';
      source: 'local';
      model: VideoCompilerCompilation;
    }
);

export type FormDataT = {
  parts: Part[];
  title: string;
  watermark: string;
  type: string;
  compilation_type: string;
};

const CreateVideoCompilation = () => {
  const { type, compilationId, compilationType } = useParams();

  const alerts = useAlerts();

  const toasts = useToasts();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const searchInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');

  const { data: compilation } = useQuery({
    queryFn: () =>
      api.get<never, VideoCompilerCompilation>(
        `/v1/video-compiler/compilations/${compilationId}`,
      ),
    queryKey: [
      'video_compiler_compilations',
      {
        id: compilationId,
      },
    ],
    enabled: !!compilationId,
  });

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') {
      return;
    }

    setSearch(e.currentTarget.value);
  };

  const {
    control,
    clearErrors,
    handleSubmit,
    setError,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      parts: [],
      title: '',
      watermark: '',
      type,
      compilation_type: compilationType,
    },
  });

  const recordType = watch('type');
  const currentCompilationType = watch('compilation_type');

  const { append, remove, move, fields } = useFieldArray({
    control,
    name: 'parts',
  });

  const onSubmit = (status: VideoCompilerCompilationStatus) => {
    clearErrors();
    return handleSubmit(async (data) => {
      const toastId = toasts.create('loading', 'Saving...');
      try {
        let compilationId: number;

        if (compilation) {
          await api.put(`/v1/video-compiler/compilations/${compilation.id}`, {
            ...data,
            status,
          });
        } else {
          const res = await api.post<object, { id: number }>(
            '/v1/video-compiler/compilations',
            {
              ...data,
              status,
            },
          );

          compilationId = res.id;
        }

        await queryClient.invalidateQueries(['video_compiler_compilations']);
        await queryClient.invalidateQueries(['video_compiler_search']);

        if (status === VideoCompilerCompilationStatus.IN_PROCESS) {
          toasts.success(toastId, 'Compilation is being generated');

          return navigate('/general/video-compilations/in-process', {
            replace: true,
          });
        }

        toasts.success(toastId, 'Compilation saved');

        if (!compilation) {
          navigate(`/general/video-compilations/${compilationId!}/edit`, {
            replace: true,
          });
        }
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
        toasts.error(toastId, 'Failed');
      }
    })();
  };

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      await api.delete(`/v1/video-compiler/compilations/${compilation?.id}`);

      await queryClient.invalidateQueries(['video_compiler_compilations']);

      alerts.close();

      return navigate('/general/video-compilations/draft', { replace: true });
    } catch {
      alerts.error();
    }
  };

  useEffect(() => {
    if (!compilation) {
      return;
    }

    reset({
      parts: compilation.parts!.map((part) => {
        if (part.type === 'tip') {
          return {
            type: 'tip',
            partId: part.model.externalId,
            fileData: part.model.fileData,
            source: 'drive',
          };
        }

        return {
          source: 'local',
          partId: part.model.id.toString(),
          type: 'version',
          model: part.model,
        };
      }),
      title: compilation.title,
      type: compilation.type,
      compilation_type: compilation.compilationType,
      watermark: '',
    });
  }, [compilation, reset]);

  if (
    !compilationId &&
    (!availableTypes.includes(type ?? '') ||
      !availableCompilationTypes.includes(compilationType ?? ''))
  ) {
    return <Navigate to="/general/video-compilations" replace />;
  }

  if (compilationId && !compilation) {
    return <ContentSpinner />;
  }

  return (
    <Page>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <Header>
        {compilation ? (
          <StyledPageHeading>
            Edit ·{' '}
            <Text as="span" $type="heading1" $color="gray">
              {compilation.type} / {compilation.compilationType}
            </Text>
          </StyledPageHeading>
        ) : (
          <StyledPageHeading>
            Create New ·{' '}
            <Text as="span" $type="heading1" $color="gray">
              {type} / {compilationType}
            </Text>
          </StyledPageHeading>
        )}

        <HeaderButtons>
          <HeaderButton
            color="secondary"
            onClick={() =>
              navigate('/general/video-compilations', { replace: true })
            }
          >
            Cancel
          </HeaderButton>
          <HeaderButton
            color="secondary"
            onClick={() => onSubmit(VideoCompilerCompilationStatus.DRAFT)}
            disabled={isSubmitting}
          >
            Save
          </HeaderButton>
          {!!compilation && (
            <Button color="danger" onClick={onDelete}>
              Delete
            </Button>
          )}
          <Button
            onClick={() => onSubmit(VideoCompilerCompilationStatus.IN_PROCESS)}
          >
            Merge
          </Button>
        </HeaderButtons>
      </Header>
      <TopBar>
        <SearchBar>
          <SearchIcon
            width="17"
            height="18"
            viewBox="0 0 17 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.58101 14.5385C11.1357 14.5385 14.0173 11.6455 14.0173 8.07693C14.0173 4.50832 11.1357 1.61539 7.58101 1.61539C4.02634 1.61539 1.14471 4.50832 1.14471 8.07693C1.14471 11.6455 4.02634 14.5385 7.58101 14.5385Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12.1321 12.6462L15.856 16.3847"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </SearchIcon>
          <SearchInput
            ref={searchInputRef}
            onKeyDown={onKeyDown}
            disabled={false}
            as="input"
            placeholder={'Insert Project ID and press Enter. Eg.: G520'}
            $type="smallBold"
            type="search"
          />
        </SearchBar>
        <Details
          control={control}
          onSubmit={() => onSubmit(VideoCompilerCompilationStatus.DRAFT)}
        />
      </TopBar>
      <Grid>
        <LeftCol>
          <SearchCard
            search={search}
            control={control}
            append={append}
            type={recordType as VideoCompilerCompilationType}
            compilationType={currentCompilationType}
          />
        </LeftCol>
        <RightCol>
          <RecipeCard
            control={control}
            remove={remove}
            move={move}
            fields={fields}
          />
        </RightCol>
      </Grid>
    </Page>
  );
};

export default CreateVideoCompilation;
